import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Download, FileSpreadsheet, Plus } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import { useAuth } from '../contexts/AuthContext';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow, Surcos } from '../components/landing/brand';
import { RECURSOS, recursoPorSlug } from '../content/recursos';
import faqGanadera from '../content/faq/planilla-ganadera.json';
import faqExistencias from '../content/faq/planilla-existencias.json';
import faqFeedlot from '../content/faq/planilla-feedlot.json';
import { ctaPublico } from '../helpers/ctaPublico';

// Planilla Excel gratuita de /recursos/:slug. Contenido en src/content/recursos.ts,
// preguntas frecuentes en src/content/faq/<slug>.json (el prerender arma el
// FAQPage desde el mismo archivo).

const FAQS: Record<string, { q: string; a: string }[]> = {
  'planilla-ganadera': faqGanadera.preguntas,
  'planilla-existencias': faqExistencias.preguntas,
  'planilla-feedlot': faqFeedlot.preguntas,
};

const RecursoPlanillaPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const recurso = recursoPorSlug(slug);
  useSeoMeta(recurso ? seoPaginas[recurso.seoKey] : seoPaginas.recursos);

  if (!recurso) return <Navigate to="/recursos" replace />;
  const faq = FAQS[recurso.slug] ?? [];
  const otras = RECURSOS.filter((r) => r.slug !== recurso.slug);

  return (
    <div className="lp">
      <PublicNav />

      <main>
        <section className="lp-ph">
          <div className="lp-wrap">
            <Eyebrow>Planilla Excel gratis</Eyebrow>
            <h1>{recurso.titulo}</h1>
            <p className="lp-lead" style={{ maxWidth: 760 }}>
              {recurso.intro}
            </p>
            <div className="lp-ctas">
              <a className="lp-btn lp-btn--primary" href={recurso.archivo} download>
                <Download aria-hidden="true" /> Descargar la planilla (.xlsx)
              </a>
              <Link className="lp-btn lp-btn--outline" to={user ? '/animals' : '/login'}>
                Llevarla en la app gratis
              </Link>
            </div>
            <p className="lp-note" style={{ marginTop: 12 }}>
              Gratis y sin registrarte. Se abre en Excel, Google Sheets o LibreOffice.
            </p>
          </div>
        </section>

        <section className="lp-sec lp-sec--white">
          <div className="lp-wrap lp-split" style={{ alignItems: 'start' }}>
            <div>
              <h2 className="lp-h3">Qué trae la {recurso.nombreCorto.toLowerCase()}</h2>
              {recurso.hojas.map((h) => (
                <div key={h.nombre} style={{ marginTop: 18 }}>
                  <h3 className="lp-h3" style={{ fontSize: 20 }}>
                    <FileSpreadsheet aria-hidden="true" style={{ display: 'inline-block', width: 20, height: 20, verticalAlign: '-3px' }} /> Hoja{' '}
                    {h.nombre}
                  </h3>
                  <p className="lp-body">{h.texto}</p>
                </div>
              ))}
            </div>
            <div>
              <h2 className="lp-h3">Cómo usarla</h2>
              <ol className="lp-body" style={{ paddingLeft: 20, listStyle: 'decimal' }}>
                {recurso.pasos.map((p) => (
                  <li key={p} style={{ marginBottom: 8 }}>
                    {p}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="lp-sec lp-dark">
          <div className="lp-wrap">
            <h2 className="lp-h2">Cuando el Excel te queda chico</h2>
            <ul className="lp-body" style={{ paddingLeft: 20, maxWidth: 760, listStyle: 'disc' }}>
              {recurso.enLaApp.map((t) => (
                <li key={t} style={{ marginBottom: 8 }}>
                  {t}
                </li>
              ))}
            </ul>
            <Link className="lp-btn lp-btn--primary" to={ctaPublico(user).to} style={{ marginTop: 16 }}>
              {ctaPublico(user).label} <ArrowRight aria-hidden="true" />
            </Link>
            <p className="lp-body" style={{ marginTop: 12 }}>
              <Link to="/de-excel-a-app" style={{ color: '#fff', textDecoration: 'underline' }}>
                Cómo pasar tu Excel a la app
              </Link>
            </p>
          </div>
          <Surcos color="blanco" style={{ right: -120, bottom: -260, width: 860 }} />
        </section>

        {faq.length > 0 && (
          <section className="lp-sec lp-faq">
            <div className="lp-wrap lp-split">
              <div className="lp-sec-h">
                <Eyebrow>Preguntas</Eyebrow>
                <h2 className="lp-h2">Preguntas frecuentes</h2>
              </div>
              <div>
                {faq.map((item, i) => (
                  <details key={item.q} open={i === 0}>
                    <summary>
                      {item.q}
                      <Plus aria-hidden="true" />
                    </summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="lp-sec lp-sec--white">
          <div className="lp-wrap">
            <h2 className="lp-h3">Otras planillas gratis</h2>
            <ul className="lp-body" style={{ paddingLeft: 20, listStyle: 'disc' }}>
              {otras.map((r) => (
                <li key={r.slug}>
                  <Link to={`/recursos/${r.slug}`}>{r.nombreCorto}</Link>: {r.resumen}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default RecursoPlanillaPage;
